import { getCountryFromHex } from './icaoCountryMap';
import { getCountryFromRegistration } from './registrationCountryMap';

/**
 * The country of registry to fly a flag for, in one place.
 *
 * Precedence — backend value, then REGISTRATION, then ICAO hex:
 *
 * 1. Whatever the backend already resolved (`countryCode` / `country_code` /
 *    `dbFlags_country`). It has the registry behind it; nothing here improves on it.
 * 2. The registration prefix. This is the authoritative statement of where an aircraft
 *    is registered — it is what the registry issued.
 * 3. The ICAO 24-bit address block, as a last resort.
 *
 * Registration MUST come before hex, and San Marino is why. ICAO allocates T7 just
 * 1,024 addresses (500000-5003FF) while the T7 register carries far more aircraft than
 * that, so T7 jets routinely broadcast a hex from outside the San Marino block. Every
 * surface that resolved the flag from the hex alone therefore showed a T7 aircraft with
 * NO flag at all — not because the San Marino flag was missing (`.flag-sm` has always
 * been in the sprite, and `T7-` has always been in the prefix map) but because the hex
 * lookup returned nothing and there was no second chance. Messages.js already had this
 * fallback; the live table, History, Predictions and Billing did not.
 *
 * @param {object} source aircraft-ish row; field names vary by table, so pass the row
 * @param {object} [fields] which keys to read when they are not the defaults
 * @returns {string|null} lower-case ISO 3166-1 alpha-2, or null when nothing resolves
 */
export function resolveCountryCode(source, fields = {}) {
  if (!source) return null;

  const {
    country = 'countryCode',
    registration = 'registration',
    hex = 'hex',
  } = fields;

  const known = source[country] || source.country_code || source.dbFlags_country;
  if (known) return String(known).toLowerCase();

  const fromReg = getCountryFromRegistration(source[registration]);
  if (fromReg) return fromReg;

  return getCountryFromHex(source[hex]) || null;
}

export default resolveCountryCode;
